import React, { useState, useEffect } from 'react';
import { FaTimes, FaUsers } from 'react-icons/fa';

export default function SaveLayoutModal({ isOpen, onClose, onSave, isAdmin, existingLayouts = [] }) {
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [overwriteLayoutId, setOverwriteLayoutId] = useState(null);
  const [saveAsNew, setSaveAsNew] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setIsPublic(false);
      setIsDefault(false);
      setOverwriteLayoutId(null);
      setSaveAsNew(true);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (!saveAsNew && !overwriteLayoutId) {
      alert('Please select a layout to overwrite or choose "Save as new"');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        isPublic: isAdmin ? isPublic : false,
        isDefault: isAdmin ? isDefault : false,
        overwriteLayoutId: saveAsNew ? null : overwriteLayoutId,
      });
      setName('');
      setIsPublic(false);
      setIsDefault(false);
      setOverwriteLayoutId(null);
      setSaveAsNew(true);
      onClose();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save layout:', error);
      }
      alert('Failed to save layout: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setName('');
      setIsPublic(false);
      setIsDefault(false);
      setOverwriteLayoutId(null);
      setSaveAsNew(true);
      onClose();
    }
  };

  const handleSaveAsNewChange = (e) => {
    setSaveAsNew(e.target.checked);
    if (e.target.checked) {
      setOverwriteLayoutId(null);
      setName('');
    }
  };

  const handleOverwriteSelect = (layoutId, layoutName) => {
    setOverwriteLayoutId(layoutId);
    setName(layoutName);
    setSaveAsNew(false);
  };

  if (!isOpen) return null;

  return (
    <div className="add-metrics-modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        className="add-metrics-modal"
        style={{ maxWidth: '28rem' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3>Save Custom Layout</h3>
          <button
            onClick={handleClose}
            disabled={saving}
            className="add-metrics-btn-cancel"
            style={{ padding: '0.25rem', minWidth: 'auto' }}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={saveAsNew}
                onChange={handleSaveAsNewChange}
                disabled={saving}
              />
              <span>Save as new layout</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.5rem' }}>
              <input
                type="radio"
                checked={!saveAsNew}
                onChange={(e) => setSaveAsNew(!e.target.checked)}
                disabled={saving}
              />
              <span>Overwrite existing layout</span>
            </label>
          </div>

          {!saveAsNew && existingLayouts.length > 0 && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Select layout to overwrite
              </label>
              <div style={{ maxHeight: '8rem', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px' }}>
                {existingLayouts.map((layout) => (
                  <button
                    key={layout.id}
                    type="button"
                    onClick={() => handleOverwriteSelect(layout.id, layout.name)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.5rem 0.75rem',
                      border: 'none',
                      background: overwriteLayoutId === layout.id ? '#f0f0f0' : 'transparent',
                      cursor: 'pointer',
                    }}
                    disabled={saving}
                  >
                    <span style={{ fontWeight: 500 }}>{layout.name}</span>
                    {layout.is_default && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#666' }}>Default</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
              Layout Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Custom Layout"
              required
              disabled={saving || (!saveAsNew && overwriteLayoutId)}
              autoFocus={saveAsNew}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
          </div>

          {isAdmin && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  disabled={saving}
                />
                <FaUsers style={{ color: '#333' }} />
                <span>Shared with team (public)</span>
              </label>
              <p style={{ fontSize: '0.75rem', color: '#666', marginLeft: '1.5rem', marginTop: '-0.5rem' }}>
                Public layouts can be used by all users
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  disabled={saving}
                />
                <span>Set as default layout</span>
              </label>
              <p style={{ fontSize: '0.75rem', color: '#666', marginLeft: '1.5rem', marginTop: '-0.5rem' }}>
                Default layout loads when opening ride view
              </p>
            </>
          )}

          {!isAdmin && (
            <p style={{ fontSize: '0.875rem', color: '#666' }}>
              This layout will be saved as private and only visible to you.
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="add-metrics-btn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="add-metrics-btn-apply"
            >
              {saving ? 'Saving...' : 'Save Layout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
